import React, { useState, useMemo } from 'react';
import { 
  ChevronLeft, 
  ChevronRight, 
  Calendar as CalendarIcon,
  Clock,
  Users,
  Video,
  Share2,
  Filter,
  Check,
  AlertCircle,
  Eye
} from 'lucide-react';
import { 
  format, 
  startOfMonth, 
  endOfMonth, 
  startOfWeek, 
  endOfWeek,
  isSameMonth,
  isSameDay,
  addMonths,
  subMonths,
  isToday,
  addWeeks,
  subWeeks,
  startOfWeek as startOfWeekFn,
  endOfWeek as endOfWeekFn,
  getDay
} from 'date-fns';
import { CalendarEvent, CalendarView, CalendarFilter } from '../types/calendar';
import { useData } from '../contexts/DataContext';
import { StatoContenuto, Contenuto, Cliente } from '../types';
import ContentSelectionModal from './ContentSelectionModal';
import { createClientColorMap } from '../utils/clientColors';

interface InteractiveCalendarProps {
  events: CalendarEvent[];
  onEventClick?: (event: CalendarEvent) => void;
  onDateClick?: (date: Date) => void;
  onCreateEvent?: (date: Date) => void;
  onContentManage?: (content: Contenuto) => void;
}

const InteractiveCalendar: React.FC<InteractiveCalendarProps> = ({
  events,
  onEventClick,
  onDateClick,
  onCreateEvent,
  onContentManage
}) => {
  const { state } = useData();
  const [view, setView] = useState<CalendarView>({
    type: 'month',
    currentDate: new Date()
  });
  const [filter, setFilter] = useState<CalendarFilter>({
    clients: [],
    eventTypes: [],
    assignedUsers: [],
    showCompleted: true
  });
  const [showFilters, setShowFilters] = useState(false);
  const [showPreferredDays, setShowPreferredDays] = useState(true);
  const [contentModalOpen, setContentModalOpen] = useState(false);
  const [selectedDateContents, setSelectedDateContents] = useState<Contenuto[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  const clientLookup = useMemo(() => {
    const map = new Map<string, Cliente>();
    state.clienti.forEach(cliente => {
      map.set(cliente.id, cliente);
    });
    return map;
  }, [state.clienti]);

  const clientColorMap = useMemo(() => createClientColorMap(state.clienti), [state.clienti]);

  const getClientColor = (clientId?: string) => {
    return clientColorMap.get(clientId || 'default');
  };

  const navigateCalendar = (direction: 'prev' | 'next') => {
    setView(prev => ({
      ...prev,
      currentDate: direction === 'next' 
        ? (prev.type === 'month' ? addMonths(prev.currentDate, 1) : addWeeks(prev.currentDate, 1))
        : (prev.type === 'month' ? subMonths(prev.currentDate, 1) : subWeeks(prev.currentDate, 1))
    }));
  };

  const getEventTypeIcon = (type: string) => {
    switch (type) {
      case 'recording':
        return <Video className="h-3 w-3" />;
      case 'deadline':
        return <Clock className="h-3 w-3" />;
      case 'publishing':
        return <Share2 className="h-3 w-3" />;
      case 'meeting':
        return <Users className="h-3 w-3" />;
      default:
        return <CalendarIcon className="h-3 w-3" />;
    }
  };

  const filteredEvents = useMemo(() => {
    // Debug: log degli eventi ricevuti
    console.log('📅 Eventi ricevuti nel calendario:', events);
    console.log('📅 Eventi filtrati:', events.filter(event => {
      if (filter.clients.length > 0 && !filter.clients.includes(event.clientId)) {
        return false;
      }
      if (filter.eventTypes.length > 0 && !filter.eventTypes.includes(event.type)) {
        return false;
      }
      if (!filter.showCompleted && event.status === 'completed') {
        return false;
      }
      return true;
    }));
    
    return events.filter(event => {
      if (filter.clients.length > 0 && !filter.clients.includes(event.clientId)) {
        return false;
      }
      if (filter.eventTypes.length > 0 && !filter.eventTypes.includes(event.type)) {
        return false;
      }
      if (!filter.showCompleted && event.status === 'completed') {
        return false;
      }
      return true;
    });
  }, [events, filter]);

  const getEventsForDate = (date: Date) => {
    const eventsForDate = filteredEvents.filter(event => 
      isSameDay(new Date(event.date), date)
    );
    
    return eventsForDate;
  };

  // Ottiene i contenuti programmati per una specifica data
  const getContentForDate = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    
    const filteredContents = state.contenuti.filter(contenuto => {
      if (!contenuto.dataPubblicazione) return false;
      
      const publicationDate = new Date(contenuto.dataPubblicazione);
      publicationDate.setHours(0, 0, 0, 0);
      
      if (isSameDay(publicationDate, checkDate)) {
        // Se la data è nel passato, mostra solo se programmato/pubblicato/archiviato
        if (checkDate < today) {
          return contenuto.stato === StatoContenuto.PROGRAMMATO || 
                 contenuto.stato === StatoContenuto.PUBBLICATO ||
                 contenuto.stato === StatoContenuto.ARCHIVIATO;
        }
        // Per date future/odierne, mostra TUTTI i contenuti che hanno una data di pubblicazione
        // indipendentemente dal loro stato attuale (inclusi BOZZA, TESTO_PRONTO, ecc.)
        // Questo permette di vedere contenuti programmati anche senza link di ispirazione
        return contenuto.stato === StatoContenuto.BOZZA ||
               contenuto.stato === StatoContenuto.TESTO_PRONTO ||
               contenuto.stato === StatoContenuto.REGISTRATO ||
               contenuto.stato === StatoContenuto.MONTATO ||
               contenuto.stato === StatoContenuto.PROGRAMMATO ||
               contenuto.stato === StatoContenuto.PUBBLICATO;
      }
      return false;
    });
    
    return filteredContents;
  };

  // Converte il numero del giorno della settimana (0=Dom, 1=Lun...) al formato del database (1=Lun, 7=Dom)
  const getDayOfWeekNumber = (date: Date): number => {
    const day = getDay(date);
    return day === 0 ? 7 : day;
  };

  // Ottiene i clienti che preferiscono un determinato giorno della settimana
  const getClientsForPreferredDay = (date: Date) => {
    const dayNumber = getDayOfWeekNumber(date);
    return state.clienti.filter(cliente => 
      cliente.giorniPreferitiRiprese && cliente.giorniPreferitiRiprese.includes(dayNumber.toString())
    );
  };

  // Verifica se un cliente ha contenuti programmati per un giorno specifico
  const hasClientContentForDay = (clienteId: string, date: Date): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    
    const contenutiCliente = state.contenuti.filter(contenuto => 
      contenuto.clienteId === clienteId &&
      contenuto.dataPubblicazione
    );
    
    const result = contenutiCliente.some(contenuto => {
      // dataPubblicazione is guaranteed to be defined due to filter above
      const publicationDate = new Date(contenuto.dataPubblicazione!);
      publicationDate.setHours(0, 0, 0, 0);
      
      if (isSameDay(publicationDate, checkDate)) {
        // Se la data è nel passato, considera automaticamente il contenuto come pubblicato/archiviato
        if (checkDate < today) {
          return contenuto.stato === StatoContenuto.PROGRAMMATO || 
                 contenuto.stato === StatoContenuto.PUBBLICATO ||
                 contenuto.stato === StatoContenuto.ARCHIVIATO;
        }
        // Per date future o odierne, considera TUTTI i contenuti con data di pubblicazione
        return contenuto.stato === StatoContenuto.BOZZA ||
               contenuto.stato === StatoContenuto.TESTO_PRONTO ||
               contenuto.stato === StatoContenuto.REGISTRATO ||
               contenuto.stato === StatoContenuto.MONTATO ||
               contenuto.stato === StatoContenuto.PROGRAMMATO ||
               contenuto.stato === StatoContenuto.PUBBLICATO;
      }
      return false;
    });
    
    return result;
  };

  // Gestisce il click su una data
  const handleDateClick = (clickedDate: Date) => {
    console.log('🔴 Click su data:', format(clickedDate, 'dd/MM/yyyy'));
    
    const dayContents = getContentForDate(clickedDate);
    const dayEvents = getEventsForDate(clickedDate);
    
    console.log('🔴 Contenuti trovati per questa data:', dayContents.length);
    console.log('🔴 Eventi trovati per questa data:', dayEvents.length);
    console.log('🔴 Contenuti:', dayContents);
    
    // Se ci sono contenuti programmati, mostra il modal per la selezione
    if (dayContents.length > 0) {
      console.log('🟢 Aprendo modal per contenuti');
      setSelectedDate(clickedDate);
      setSelectedDateContents(dayContents);
      setContentModalOpen(true);
    } else {
      console.log('🟡 Nessun contenuto trovato, usando callback');
      // Se non ci sono contenuti ma c'è la callback onDateClick, usala
      onDateClick?.(clickedDate);
    }
  };

  // Gestisce la selezione di un contenuto dal modal
  const handleContentSelect = (content: Contenuto) => {
    // Qui potresti implementare logica per mostrare i dettagli del contenuto
    // Per ora chiudiamo semplicemente il modal
    setContentModalOpen(false);
    console.log('Contenuto selezionato:', content);
  };

  const handleManageContent = (content: Contenuto) => {
    setContentModalOpen(false);
    onContentManage?.(content);
  };

  // Ottiene lo stato di copertura per un giorno preferito
  const getPreferredDayStatus = (date: Date) => {
    const clientsForDay = getClientsForPreferredDay(date);
    if (clientsForDay.length === 0) return null;

    const coveredClients = clientsForDay.filter(cliente => 
      hasClientContentForDay(cliente.id, date)
    );
    const uncoveredClients = clientsForDay.filter(cliente => 
      !hasClientContentForDay(cliente.id, date)
    );

    if (coveredClients.length === clientsForDay.length) {
      return { type: 'fully_covered', clients: clientsForDay, coveredClients, uncoveredClients };
    } else if (coveredClients.length === 0) {
      return { type: 'not_covered', clients: clientsForDay, coveredClients, uncoveredClients };
    } else {
      return { type: 'partially_covered', clients: clientsForDay, coveredClients, uncoveredClients };
    }
  };

  const generateDaysInInterval = (start: Date, end: Date): Date[] => {
    const days: Date[] = [];
    const current = new Date(start);
    
    while (current <= end) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    
    return days;
  };

  const renderMonthView = () => {
    const monthStart = startOfMonth(view.currentDate);
    const monthEnd = endOfMonth(view.currentDate);
    const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });

    const days = generateDaysInInterval(calendarStart, calendarEnd);

    // Nomi abbreviati dei giorni per mobile
    const dayNamesShort = ['L', 'M', 'M', 'G', 'V', 'S', 'D'];
    const dayNamesFull = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];

    return (
      <div className="grid grid-cols-7 gap-px bg-gray-200 rounded-lg overflow-hidden overflow-x-auto touch-pan-x">
        {/* Header giorni della settimana */}
        {dayNamesFull.map((day, index) => (
          <div key={day} className="bg-gray-50 p-1 sm:p-2 text-center text-[10px] sm:text-xs md:text-sm font-medium text-gray-500">
            <span className="hidden sm:inline">{day}</span>
            <span className="sm:hidden">{dayNamesShort[index]}</span>
          </div>
        ))}
        
        {/* Giorni del calendario */}
        {days.map((day: Date) => {
          const dayEvents = getEventsForDate(day);
          const dayContents = getContentForDate(day);
          const dayClients = Array.from(
            new Set(dayContents.map(content => content.clienteId).filter((id): id is string => Boolean(id)))
          ).map(clientId => {
            const cliente = clientLookup.get(clientId);
            return {
              id: clientId,
              nome: cliente?.nomeAzienda || 'Cliente',
              colori: getClientColor(clientId)
            };
          });
          const dominantColor = dayClients[0]?.colori;
          const isCurrentMonth = isSameMonth(day, view.currentDate);
          const isCurrentDay = isToday(day);

          return (
            <div
              key={day.toString()}
              className={`
                min-h-16 sm:min-h-20 md:min-h-24 lg:min-h-32 p-0.5 sm:p-1 md:p-2 cursor-pointer transition-colors relative border-l-2 sm:border-l-4
                active:bg-gray-100 touch-manipulation
                ${!isCurrentMonth ? 'opacity-40' : ''}
                ${isCurrentDay ? 'bg-yellow-50 ring-1 sm:ring-2 ring-yellow-200' : ''}
                ${(() => {
                  if (!showPreferredDays) return 'bg-white';
                  const status = getPreferredDayStatus(day);
                  if (!status) return 'bg-white';
                  if (status.type === 'fully_covered') return 'bg-green-50';
                  if (status.type === 'not_covered') return 'bg-orange-50';
                  return 'bg-white';
                })()}
              `}
              onClick={() => handleDateClick(day)}
              style={{
                borderLeftColor: dominantColor?.primary || 'transparent',
                backgroundColor: dominantColor?.background && !isCurrentDay ? dominantColor.background : undefined
              }}
            >
              <div className="flex items-center justify-between mb-0.5 sm:mb-1">
                <span className={`
                  text-[10px] sm:text-xs md:text-sm font-medium
                  ${isCurrentDay ? 'text-yellow-800 bg-yellow-200 rounded-full w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center' : 'text-gray-900'}
                  ${!isCurrentMonth ? 'text-gray-400' : ''}
                `}>
                  {format(day, 'd')}
                </span>
                <div className="flex items-center gap-0.5 sm:space-x-1">
                  {showPreferredDays && (() => {
                    const status = getPreferredDayStatus(day);
                    if (!status) return null;
                    if (status.type === 'fully_covered') {
                      return (
                        <div className="flex items-center" title={`Tutti i clienti hanno contenuti: ${status.clients.map(c => c.nomeAzienda).join(', ')}`}>
                          <Check className="h-2.5 w-2.5 sm:h-3 sm:w-3 text-green-600" />
                        </div>
                      );
                    }
                    if (status.type === 'not_covered') {
                      return (
                        <div className="flex items-center" title={`Clienti senza contenuti: ${status.clients.map(c => c.nomeAzienda).join(', ')}`}>
                          <AlertCircle className="h-2.5 w-2.5 sm:h-3 sm:w-3 text-orange-600" />
                        </div>
                      );
                    }
                    return null;
                  })()}
                  {/* Indicatore per contenuti programmati */}
                  {dayContents.length > 0 && (
                    <span className="text-[8px] sm:text-[10px] md:text-xs bg-purple-100 text-purple-800 rounded-full px-0.5 sm:px-1 font-medium">
                      {dayContents.length}
                    </span>
                  )}
                  {dayEvents.length > 0 && (
                    <span className="text-[8px] sm:text-[10px] md:text-xs bg-blue-100 text-blue-800 rounded-full px-0.5 sm:px-1">
                      {dayEvents.length}
                    </span>
                  )}
                </div>
              </div>

              <div className="space-y-0.5 sm:space-y-1 overflow-hidden">
                {dayClients.length > 0 && (
                  <div className="flex flex-wrap gap-0.5 sm:gap-1">
                    {dayClients.slice(0, window.innerWidth < 640 ? 1 : 3).map(({ id, nome, colori }) => (
                      <span
                        key={id}
                        className="text-[7px] sm:text-[9px] md:text-[10px] font-semibold px-1 sm:px-2 py-0 sm:py-0.5 rounded-full border truncate max-w-full"
                        style={{
                          backgroundColor: colori.background,
                          color: colori.primary,
                          borderColor: colori.primary
                        }}
                        title={nome}
                      >
                        <span className="hidden sm:inline">{nome.length > 14 ? `${nome.slice(0, 12)}...` : nome}</span>
                        <span className="sm:hidden">{nome.length > 6 ? `${nome.slice(0, 5)}...` : nome}</span>
                      </span>
                    ))}
                    {dayClients.length > (window.innerWidth < 640 ? 1 : 3) && (
                      <span className="text-[7px] sm:text-[9px] text-gray-500">+{dayClients.length - (window.innerWidth < 640 ? 1 : 3)}</span>
                    )}
                  </div>
                )}
                {/* Mostra clienti con giorni preferiti - nascosto su mobile per spazio */}
                {showPreferredDays && isCurrentMonth && (() => {
                  const status = getPreferredDayStatus(day);
                  if (!status || status.type === 'partially_covered') return null;

                  return (
                    <div className="hidden sm:block space-y-0.5 sm:space-y-1">
                      {status.clients.slice(0, window.innerWidth < 768 ? 1 : 2).map(cliente => {
                        const hasContent = hasClientContentForDay(cliente.id, day);
                        return (
                          <div
                            key={cliente.id}
                            className={`text-[9px] sm:text-[10px] md:text-xs p-0.5 sm:p-1 rounded border-l-2 ${
                              hasContent
                                ? 'bg-green-100 text-green-800 border-green-400'
                                : 'bg-orange-100 text-orange-800 border-orange-400'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <span className="truncate font-medium">{cliente.nomeAzienda}</span>
                              {hasContent ? (
                                <Check className="h-2.5 w-2.5 sm:h-3 sm:w-3 flex-shrink-0" />
                              ) : (
                                <AlertCircle className="h-2.5 w-2.5 sm:h-3 sm:w-3 flex-shrink-0" />
                              )}
                            </div>
                          </div>
                        );
                      })}
                      {status.clients.length > (window.innerWidth < 768 ? 1 : 2) && (
                        <div className="text-[9px] sm:text-[10px] md:text-xs text-gray-500 p-0.5 sm:p-1">
                          +{status.clients.length - (window.innerWidth < 768 ? 1 : 2)} altri
                        </div>
                      )}
                    </div>
                  );
                })()}
                
                {/* Mostra eventi normali - meno su mobile */}
                {dayEvents.slice(0, window.innerWidth < 640 ? 1 : window.innerWidth < 768 ? 2 : 3).map(event => {
                  const cliente = state.clienti.find(c => c.id === event.clientId);
                  const coloriEvento = getClientColor(event.clientId);
                  return (
                    <div
                      key={event.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        onEventClick?.(event);
                      }}
                      className="text-[8px] sm:text-[10px] md:text-xs p-0.5 sm:p-1 rounded border-l-2 cursor-pointer hover:shadow-sm active:shadow-md transition-shadow text-gray-900 touch-manipulation"
                      title={`${event.title}${cliente ? ` - ${cliente.nomeAzienda}` : ''}`}
                      style={{
                        borderLeftColor: coloriEvento.primary,
                        backgroundColor: coloriEvento.background
                      }}
                    >
                      <div className="flex items-center gap-0.5 sm:space-x-1">
                        <span
                          className="hidden sm:inline-flex h-1.5 w-1.5 sm:h-2 sm:w-2 rounded-full flex-shrink-0"
                          style={{ backgroundColor: coloriEvento.primary }}
                        />
                        <span className="hidden md:inline">{getEventTypeIcon(event.type)}</span>
                        <div className="flex-1 min-w-0">
                          <div className="truncate font-medium">
                            <span className="hidden sm:inline">{event.title.length > 20 ? event.title.substring(0, 20) + '...' : event.title}</span>
                            <span className="sm:hidden">{event.title.length > 8 ? event.title.substring(0, 8) + '...' : event.title}</span>
                          </div>
                          {cliente && (
                            <div className="hidden md:block truncate opacity-75">
                              {cliente.nomeAzienda}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {dayEvents.length > (window.innerWidth < 640 ? 1 : window.innerWidth < 768 ? 2 : 3) && (
                  <div className="text-[8px] sm:text-[10px] md:text-xs text-gray-500 p-0.5 sm:p-1">
                    +{dayEvents.length - (window.innerWidth < 640 ? 1 : window.innerWidth < 768 ? 2 : 3)} altri
                  </div>
                )}
              </div>
              
              {onCreateEvent && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onCreateEvent(day);
                  }}
                  className="hidden md:block w-full mt-0.5 sm:mt-1 p-0.5 sm:p-1 text-[8px] sm:text-[10px] md:text-xs text-gray-400 hover:text-gray-600 border border-dashed border-gray-300 rounded hover:border-gray-400 active:bg-gray-100 transition-colors touch-manipulation"
                >
                  + Aggiungi
                </button>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderWeekView = () => {
    const weekStart = startOfWeekFn(view.currentDate, { weekStartsOn: 1 });
    const weekEnd = endOfWeekFn(view.currentDate, { weekStartsOn: 1 });
    const days = generateDaysInInterval(weekStart, weekEnd);

    return (
      <div className="overflow-x-auto touch-pan-x -mx-2 px-2">
        <div className="grid grid-cols-7 gap-1 sm:gap-2 min-w-[500px] sm:min-w-0">
          {days.map((day: Date) => {
            const dayEvents = getEventsForDate(day);
            const isCurrentDay = isToday(day);

            return (
              <div key={day.toString()} className="space-y-1 sm:space-y-2">
                <div className={`
                  p-1 sm:p-2 text-center rounded-lg cursor-pointer hover:bg-gray-50 active:bg-gray-100 touch-manipulation
                  ${isCurrentDay ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-50'}
                `} onClick={() => onDateClick?.(day)}>
                  <div className="text-[10px] sm:text-xs font-medium">
                    {format(day, 'EEE')}
                  </div>
                  <div className="text-sm sm:text-base md:text-lg font-bold">
                    {format(day, 'd')}
                  </div>
                </div>

                <div className="space-y-1 min-h-48 sm:min-h-64 md:min-h-96">
                  {dayEvents.map(event => {
                    const coloriEvento = getClientColor(event.clientId);
                    return (
                      <div
                        key={event.id}
                        onClick={() => onEventClick?.(event)}
                        className="p-1 sm:p-2 rounded border-l-2 sm:border-l-4 cursor-pointer hover:shadow-md active:shadow-lg transition-shadow text-gray-900 touch-manipulation"
                        style={{
                          borderLeftColor: coloriEvento.primary,
                          backgroundColor: coloriEvento.background
                        }}
                      >
                        <div className="flex items-center gap-0.5 sm:space-x-1 mb-0.5 sm:mb-1">
                          <span
                            className="hidden sm:inline-flex h-1.5 w-1.5 sm:h-2 sm:w-2 rounded-full"
                            style={{ backgroundColor: coloriEvento.primary }}
                          />
                          <span className="hidden md:inline">{getEventTypeIcon(event.type)}</span>
                          <span className="text-[10px] sm:text-xs font-medium truncate">{event.title}</span>
                        </div>
                        {event.description && (
                          <p className="hidden sm:block text-[10px] sm:text-xs opacity-75 truncate">{event.description}</p>
                        )}
                      </div>
                    );
                  })}

                  {onCreateEvent && (
                    <button
                      onClick={() => onCreateEvent(day)}
                      className="w-full p-1 sm:p-2 text-[10px] sm:text-xs text-gray-400 hover:text-gray-600 border border-dashed border-gray-300 rounded hover:border-gray-400 active:bg-gray-100 transition-colors touch-manipulation"
                    >
                      <span className="hidden sm:inline">+ Aggiungi Evento</span>
                      <span className="sm:hidden">+</span>
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-2 sm:space-y-4">
      {/* Header controlli - layout responsive */}
      <div className="flex flex-col gap-2 sm:gap-4 md:flex-row md:items-center md:justify-between">
        {/* Navigazione e titolo */}
        <div className="flex items-center justify-between sm:justify-start gap-2 sm:gap-4">
          <div className="flex items-center">
            <button
              onClick={() => navigateCalendar('prev')}
              className="p-1.5 sm:p-2 hover:bg-gray-100 active:bg-gray-200 rounded-lg transition-colors touch-manipulation"
            >
              <ChevronLeft className="h-4 w-4 sm:h-5 sm:w-5" />
            </button>

            <h2 className="text-sm sm:text-base md:text-xl font-bold text-gray-900 min-w-24 sm:min-w-36 md:min-w-48 text-center px-1 sm:px-2">
              {format(view.currentDate, view.type === 'month' ? 'MMM yyyy' : 'dd MMM yyyy')}
            </h2>

            <button
              onClick={() => navigateCalendar('next')}
              className="p-1.5 sm:p-2 hover:bg-gray-100 active:bg-gray-200 rounded-lg transition-colors touch-manipulation"
            >
              <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5" />
            </button>
          </div>

          <button
            onClick={() => setView(prev => ({ ...prev, currentDate: new Date() }))}
            className="px-2 sm:px-3 py-1 text-xs sm:text-sm bg-yellow-100 text-yellow-800 rounded-lg hover:bg-yellow-200 active:bg-yellow-300 transition-colors touch-manipulation whitespace-nowrap"
          >
            Oggi
          </button>
        </div>

        {/* Controlli vista e filtri */}
        <div className="flex items-center justify-between sm:justify-end gap-1 sm:gap-2">
          <div className="flex bg-gray-100 rounded-lg p-0.5 sm:p-1">
            <button
              onClick={() => setView(prev => ({ ...prev, type: 'month' }))}
              className={`px-2 sm:px-3 py-1 text-xs sm:text-sm rounded transition-colors touch-manipulation ${
                view.type === 'month'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Mese
            </button>
            <button
              onClick={() => setView(prev => ({ ...prev, type: 'week' }))}
              className={`px-2 sm:px-3 py-1 text-xs sm:text-sm rounded transition-colors touch-manipulation ${
                view.type === 'week'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <span className="hidden sm:inline">Settimana</span>
              <span className="sm:hidden">Sett.</span>
            </button>
          </div>

          <button
            onClick={() => setShowPreferredDays(!showPreferredDays)}
            className={`p-1.5 sm:p-2 hover:bg-gray-100 active:bg-gray-200 rounded-lg transition-colors touch-manipulation ${
              showPreferredDays ? 'bg-yellow-100 text-yellow-800' : ''
            }`}
            title="Mostra/Nascondi giorni preferiti clienti"
          >
            <Eye className="h-4 w-4 sm:h-5 sm:w-5" />
          </button>

          <button
            onClick={() => setShowFilters(!showFilters)}
            className="p-1.5 sm:p-2 hover:bg-gray-100 active:bg-gray-200 rounded-lg transition-colors touch-manipulation"
          >
            <Filter className="h-4 w-4 sm:h-5 sm:w-5" />
          </button>
        </div>
      </div>

      {/* Legenda giorni preferiti - responsive */}
      {showPreferredDays && (
        <div className="bg-white p-2 sm:p-4 rounded-lg border border-gray-200">
          <h3 className="text-xs sm:text-sm font-medium text-gray-900 mb-2 sm:mb-3">Legenda Calendario</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-4">
            <div className="flex items-center gap-1.5 sm:space-x-2">
              <div className="w-3 h-3 sm:w-4 sm:h-4 bg-green-50 border border-green-200 rounded flex-shrink-0"></div>
              <Check className="h-3 w-3 sm:h-4 sm:w-4 text-green-600 flex-shrink-0" />
              <span className="text-[10px] sm:text-xs md:text-sm text-gray-700">Tutti coperti</span>
            </div>
            <div className="flex items-center gap-1.5 sm:space-x-2">
              <div className="w-3 h-3 sm:w-4 sm:h-4 bg-orange-50 border border-orange-200 rounded flex-shrink-0"></div>
              <AlertCircle className="h-3 w-3 sm:h-4 sm:w-4 text-orange-600 flex-shrink-0" />
              <span className="text-[10px] sm:text-xs md:text-sm text-gray-700">Da programmare</span>
            </div>
            <div className="flex items-center gap-1.5 sm:space-x-2">
              <div className="w-3 h-3 sm:w-4 sm:h-4 bg-purple-100 border border-purple-200 rounded flex items-center justify-center flex-shrink-0">
                <span className="text-[8px] sm:text-xs">N</span>
              </div>
              <span className="text-[10px] sm:text-xs md:text-sm text-gray-700">N contenuti</span>
            </div>
          </div>
        </div>
      )}

      {/* Filtri - responsive */}
      {showFilters && (
        <div className="bg-white p-2 sm:p-4 rounded-lg border border-gray-200">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                Tipi di Evento
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-1 gap-1 sm:space-y-2">
                {['recording', 'deadline', 'publishing', 'meeting'].map(type => (
                  <label key={type} className="flex items-center touch-manipulation">
                    <input
                      type="checkbox"
                      checked={filter.eventTypes.includes(type)}
                      onChange={(e) => {
                        setFilter(prev => ({
                          ...prev,
                          eventTypes: e.target.checked
                            ? [...prev.eventTypes, type]
                            : prev.eventTypes.filter(t => t !== type)
                        }));
                      }}
                      className="rounded border-gray-300 text-yellow-600 focus:ring-yellow-500 h-4 w-4 sm:h-auto sm:w-auto"
                    />
                    <span className="ml-1.5 sm:ml-2 text-[10px] sm:text-sm text-gray-700">
                      {type === 'recording' && 'Registrazione'}
                      {type === 'deadline' && 'Scadenza'}
                      {type === 'publishing' && 'Pubblicazione'}
                      {type === 'meeting' && 'Riunione'}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex items-start sm:items-center">
              <label className="flex items-center touch-manipulation">
                <input
                  type="checkbox"
                  checked={filter.showCompleted}
                  onChange={(e) => setFilter(prev => ({ ...prev, showCompleted: e.target.checked }))}
                  className="rounded border-gray-300 text-yellow-600 focus:ring-yellow-500 h-4 w-4 sm:h-auto sm:w-auto"
                />
                <span className="ml-1.5 sm:ml-2 text-[10px] sm:text-sm text-gray-700">
                  Mostra Completati
                </span>
              </label>
            </div>
          </div>
        </div>
      )}

      {/* Calendario - responsive container */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-2 sm:p-4 overflow-hidden">
        {view.type === 'month' ? renderMonthView() : renderWeekView()}
      </div>

      {/* Eventi di oggi - responsive */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-2 sm:p-4">
        <h3 className="text-sm sm:text-base md:text-lg font-semibold text-gray-900 mb-2 sm:mb-3 flex items-center">
          <CalendarIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-1.5 sm:mr-2" />
          Eventi di Oggi
        </h3>

        {getEventsForDate(new Date()).length === 0 ? (
          <p className="text-gray-500 text-xs sm:text-sm">Nessun evento programmato per oggi</p>
        ) : (
          <div className="space-y-1.5 sm:space-y-2">
            {getEventsForDate(new Date()).map(event => {
              const coloriEvento = getClientColor(event.clientId);
              return (
                <div
                  key={event.id}
                  onClick={() => onEventClick?.(event)}
                  className="p-2 sm:p-3 rounded-lg border-l-2 sm:border-l-4 cursor-pointer hover:shadow-md active:shadow-lg transition-all text-gray-900 touch-manipulation"
                  style={{
                    borderLeftColor: coloriEvento.primary,
                    backgroundColor: coloriEvento.background
                  }}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5 sm:space-x-2 min-w-0">
                      <span
                        className="inline-flex h-2 w-2 sm:h-2.5 sm:w-2.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: coloriEvento.primary }}
                      />
                      <span className="hidden sm:inline">{getEventTypeIcon(event.type)}</span>
                      <span className="font-medium text-xs sm:text-sm md:text-base truncate">{event.title}</span>
                    </div>
                    <span className="text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 bg-white bg-opacity-60 rounded whitespace-nowrap flex-shrink-0">
                      {event.status === 'completed' ? 'Fatto' : 'Prog.'}
                    </span>
                  </div>
                  {event.description && (
                    <p className="text-[10px] sm:text-xs md:text-sm mt-1 opacity-75 truncate">{event.description}</p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      
      {/* Modal per selezione contenuti */}
      {selectedDate && (
        <ContentSelectionModal
          isOpen={contentModalOpen}
          onClose={() => setContentModalOpen(false)}
          date={selectedDate}
          contents={selectedDateContents}
          onSelectContent={handleContentSelect}
          onManageContent={handleManageContent}
        />
      )}
    </div>
  );
};

export default InteractiveCalendar;
